import React, { FC, HTMLAttributes } from 'react';
import cx from 'classnames';
import {
  Experiment,
  FeatureFlags,
  LogoVariants,
  Navigation as NavigationType,
  Theme,
  User
} from '../types';
import { FeatureProvider } from '../utils/features';
import { TranslationProvider } from '../utils/translations';
import { ExperimentProvider } from '../utils/experiments';
import { CurrentUserProvider } from '../utils/currentUser';
import AppBar from './AppBar';
import Home from './Home';
import Navigation from './Navigation';
import { ThemeProvider } from '../utils/theme';
import styles from './Header.css';
import { kijijiLogo } from '../config';

export enum Tag {
  HEADER = 'header',
  DIV = 'div',
  NAV = 'nav'
}

type Props = {
  /**
   * custom styles
   * - root: main wrapper style
   * - appbar: app bar wrapper style
   * - logo: logo image style
   * - navigation: style for main navigation
   * - action: style for main navigation items of type 'action'
   * - link: style for navigation items of type link, route and masked link
   */
  classNames?: {
    root?: string;
    appbar?: string;
    logo?: string;
    navigation?: string;
    action?: string;
    link?: string;
  };
  /**
   * logged in user
   */
  currentUser?: User;
  /**
   * current a/b tests, e.g. by optimizely
   */
  experiments?: Experiment[];
  /**
   * feature flag settings of the app, hashmap of feature name => value
   */
  featureFlags?: FeatureFlags;
  /**
   * current user has unread messages
   */
  hasUnreadMessages?: boolean;
  /**
   * current user has unread notifications
   */
  hasUnreadNotifications?: boolean;
  /**
   * url to webapp home page, used for clicks on the logo, also see onLogoClick
   */
  homeUrl: string;
  /**
   * current locale
   */
  locale: string;
  /**
   * translations for texts and labels
   */
  messages: { [key: string]: string };
  /**
   * logo definitions for small and large viewports
   */
  logo?: LogoVariants;
  /**
   * navigation definition with menus
   *
   * - icon="<name>" will render a icon from the asset catalog
   * - icon="UserIcon" will render the user's avatar with badge for unread messages
   * - icon=<CustomIcon /> will render a custom icon
   * - label="username|account_headline" will render the username, fallback "account_headline"
   */
  navigation?: NavigationType;
  /**
   * wrapper tag
   * @default Tag.DIV
   */
  tagName?: Tag;
  /**
   * theme to be used for rendering, e.g.
   * - default: light navbar
   * - brand: brand color navbar
   * - takeover: styled for (homepage) takeover
   * - ghost: transparent for use with other app backgrounds
   * usage of theme may depend on the viewport, e.g. branded theme is never used for small / medium views
   * @default Theme.DEFAULT
   */
  theme?: Theme;
  /**
   * handle click on logo (optional) to change standard behaviour
   */
  onLogoClick?: HTMLAttributes<HTMLAnchorElement>['onClick'];
  /**
   * handle click on navigation (button, menu, menu item etc.)
   * @param name  the name of the navigation item
   * @param details additional data defined by the clicked item
   * @param event click event
   * @param url target
   */
  onNavigationClick?: (
    name: string,
    details?: { [key: string]: string | undefined },
    event?: Event
  ) => void;
};

/**
 * Shared web header for all tenants
 */
const Header: FC<Props> = ({
  classNames,
  children,
  currentUser,
  experiments,
  featureFlags = {},
  hasUnreadMessages,
  hasUnreadNotifications,
  homeUrl,
  locale,
  logo = kijijiLogo,
  messages,
  navigation,
  tagName = Tag.NAV,
  theme = Theme.DEFAULT,
  onNavigationClick,
  onLogoClick
}): JSX.Element => (
  <ThemeProvider theme={theme}>
    <TranslationProvider locale={locale} messages={messages}>
      <CurrentUserProvider
        hasUnreadMessages={hasUnreadMessages}
        hasUnreadNotifications={hasUnreadNotifications}
        user={currentUser}
      >
        <ExperimentProvider values={experiments}>
          <FeatureProvider values={featureFlags}>
            <div
              className={cx(
                styles.component,
                {
                  [styles.branded]: theme === Theme.BRAND,
                  [styles.takeover]: theme === Theme.TAKEOVER,
                  [styles.ghost]: theme === Theme.GHOST
                },
                classNames?.root
              )}
            >
              <AppBar className={classNames?.appbar} tagName={tagName}>
                <Home
                  classNames={{
                    logo: classNames?.logo
                  }}
                  label="home"
                  logo={logo}
                  url={homeUrl}
                  onClick={onLogoClick}
                />
                <Navigation
                  classNames={{
                    root: classNames?.navigation,
                    action: classNames?.action,
                    link: classNames?.link
                  }}
                  navigation={navigation}
                  onClick={onNavigationClick}
                >
                  {children}
                </Navigation>
              </AppBar>
            </div>
          </FeatureProvider>
        </ExperimentProvider>
      </CurrentUserProvider>
    </TranslationProvider>
  </ThemeProvider>
);

export default Header;
